import React from 'react';
import type { Types } from '@ohif/core';
import { id } from './id.js';
import './brand.css';
import { Icons } from '@ohif/ui-next';
import {
  LaunchInfo,
  LaunchArrow,
  CheckBoxChecked,
  CheckBoxUnchecked,
  Magnifier,
  SycaiLogo,
} from './components/BrandIcons';
import { StudyListBrand } from './components/StudyListBrand';
import { showBootSplash } from './components/BootSplash';
import getCommandsModule from './getCommandsModule';
import getCustomizationModule from './getCustomizationModule';
import getToolbarModule from './getToolbarModule';
import getViewportModule from './getViewportModule';
import { SycaiService } from './services/SycaiService';
import { AccountRoleEnum, SessionService } from './services/SessionService';
import { ReportAssessmentService } from './services/ReportAssessmentService';
import { DiagnosisService } from './services/DiagnosisService';
import { OrganReviewService } from './services/OrganReviewService';
import { SeriesLabelService } from './services/SeriesLabelService';
import { setViewerUnauthorizedHandler, UnauthorizedException } from './http';
import { describeError, fatal, readCookie, rerootBulkDataUri } from './utils';
import { initRoots } from './config/roots';
import {
  DiagnosisCompleteVariant,
  showDiagnosisCompleteDialog,
} from './components/dialogs/DiagnosisCompleteDialog';

function applyPacsRootFromCookie(
  extensionManager?: Types.Extensions.ExtensionParams['extensionManager']
) {
  const pacsUrl = readCookie('pacsUrl');

  if (pacsUrl === null) {
    return { applied: false, sourceName: extensionManager?.activeDataSourceName };
  }

  const root = pacsUrl.trim().replace(/\/+$/, '');
  if (!root) {
    fatal('the pacsUrl cookie is present but empty; refusing to fall back to relative roots');
  }

  if (!extensionManager) {
    fatal('the pacsUrl cookie is set but preRegistration supplied no extensionManager');
  }

  const sourceName = extensionManager.activeDataSourceName;
  const dataSource = extensionManager.getActiveDataSource()?.[0];

  if (!dataSource) {
    fatal(
      `active data source "${sourceName}" is not registered at preRegistration time. ` +
        `Either the name is wrong in the app config, or @ohif/extension-sycai was ` +
        `moved ahead of @ohif/extension-default in pluginConfig.json.`
    );
  }

  const configuration = extensionManager.getDataSourceDefinition(sourceName)?.configuration;

  if (!configuration) {
    fatal(
      `active data source "${sourceName}" has no configuration to extend; refusing to ` +
        `replace it with the three roots alone.`
    );
  }

  extensionManager.updateDataSourceConfiguration(sourceName, {
    ...configuration,
    wadoUriRoot: `${root}/wado`,
    wadoRoot: `${root}/dicom-web`,
    qidoRoot: `${root}/dicom-web`,
    bulkDataURI: {
      enabled: true,
      transform: (bulkDataUri: string) => rerootBulkDataUri(bulkDataUri, root),
      startsWith: root,
      prefixWith: root,
    },
  });

  return { applied: true, sourceName };
}

const STUDY_LIST_PREVIEW_KEY = 'studyList.previewOpen';

function seedStudyListPreviewClosed(): void {
  if (window.sessionStorage.getItem(STUDY_LIST_PREVIEW_KEY) === null) {
    window.sessionStorage.setItem(STUDY_LIST_PREVIEW_KEY, JSON.stringify({ open: false }));
  }
}

const unsubscriptions: Array<() => void> = [];

const sycaiExtension = {
  /** Only required property. Unique across all extensions. */
  id,

  async preRegistration({
    servicesManager,
    extensionManager,
    appConfig,
  }: Types.Extensions.ExtensionParams) {
    // The icon set's kebab aliases close over the stock imports, so both forms are registered.
    Icons.addIcon('LaunchInfo', LaunchInfo);
    Icons.addIcon('launch-info', LaunchInfo);
    Icons.addIcon('LaunchArrow', LaunchArrow);
    Icons.addIcon('CheckBoxChecked', CheckBoxChecked);
    Icons.addIcon('checkbox-checked', CheckBoxChecked);
    Icons.addIcon('checkbox-active', CheckBoxChecked);
    Icons.addIcon('CheckBoxUnchecked', CheckBoxUnchecked);
    Icons.addIcon('checkbox-unchecked', CheckBoxUnchecked);
    Icons.addIcon('checkbox-default', CheckBoxUnchecked);
    Icons.addIcon('Magnifier', Magnifier);
    Icons.addIcon('magnifier', Magnifier);

    seedStudyListPreviewClosed();

    const whiteLabeling = (appConfig.whiteLabeling ??= {});
    whiteLabeling.createLogoComponentFn = () => <StudyListBrand />;

    servicesManager.registerService(SycaiService.REGISTRATION);

    const { sycaiService } = servicesManager.services;
    console.info(sycaiService.getBanner());

    initRoots(extensionManager);

    // Before auth, deliberately: a session failure must not be able to stop the
    // study list from loading.
    applyPacsRootFromCookie(extensionManager);

    servicesManager.registerService(SessionService.REGISTRATION);

    servicesManager.registerService(ReportAssessmentService.REGISTRATION);
    servicesManager.registerService(DiagnosisService.REGISTRATION);
    servicesManager.registerService(OrganReviewService.REGISTRATION);

    servicesManager.registerService(SeriesLabelService.REGISTRATION);

    const { sessionService, userAuthenticationService } = servicesManager.services;

    userAuthenticationService.setServiceImplementation({
      getAuthorizationHeader: () => {
        const accessToken = readCookie('accessToken');
        return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
      },
      handleUnauthenticated: () => {
        sessionService.redirectToLogin();
      },
    });

    setViewerUnauthorizedHandler(() => userAuthenticationService.handleUnauthenticated());

    let account;

    try {
      account = await sessionService.verifySession();
    } catch (error) {
      if (error instanceof UnauthorizedException) {
        await showBootSplash();
        userAuthenticationService.handleUnauthenticated();

        // Never resolves, deliberately. Returning here would let appInit finish and
        // mount the viewer shell over the splash for as long as the browser takes
        // to leave for the login page — the flash this whole path exists to remove.
        await new Promise<never>(() => {});
      }

      console.error(
        `[sycai] session verification failed ${describeError(error)}; ` +
          `continuing without a session`
      );
      return;
    }

    appConfig.disableConfirmationPrompts = account.role === AccountRoleEnum.REVIEWER;
  },

  onModeEnter({ servicesManager }: { servicesManager: AppTypes.ServicesManager }) {
    const { diagnosisService, uiModalService } = servicesManager.services;

    const modalService = uiModalService as AppTypes.UIModalService;

    const raise = (variant: DiagnosisCompleteVariant) =>
      ({ studyInstanceUid }: { studyInstanceUid: string }) => {
        if (studyInstanceUid !== diagnosisService.getTrackedStudy()) {
          return;
        }

        showDiagnosisCompleteDialog(modalService, variant);
      };

    unsubscriptions.push(
      diagnosisService.subscribe(DiagnosisService.EVENTS.DIAGNOSIS_COMPLETED, raise('completed'))
        .unsubscribe,
      diagnosisService.subscribe(DiagnosisService.EVENTS.DIAGNOSIS_FAILED, raise('failed'))
        .unsubscribe
    );
  },

  onModeExit() {
    unsubscriptions.forEach(unsubscribe => unsubscribe());
    unsubscriptions.length = 0;
  },

  getToolbarModule,
  getCustomizationModule,
  getCommandsModule,
  getViewportModule,
};

export { default as InvestigationalUseDialog } from './components/InvestigationalUseDialog';

export default sycaiExtension;
